import React, { useState, useEffect } from "react";
import { Card, Table, Tag, Button, Input, message, Spin, Row, Col, Modal, Space } from "antd";
import {
  CheckOutlined,
  CloseOutlined,
  SearchOutlined,
} from "@ant-design/icons";
import styled from "styled-components";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { API_CONST } from "../../../const";

const Wrapper = styled.div`
  .page-header {
    margin-bottom: 24px;
    h1 {
      font-size: 22px;
      font-weight: 700;
      color: #262626;
      margin: 0 0 4px;
    }
    p {
      color: #8c8c8c;
      margin: 0;
    }
  }
  .stat-card {
    border-radius: 8px;
  }
  .stat-inner {
    display: flex;
    align-items: center;
    gap: 16px;
  }
  .stat-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 22px;
  }
  .stat-info h3 {
    font-size: 24px;
    font-weight: 700;
    margin: 0;
    color: #262626;
  }
  .stat-info p {
    margin: 0;
    color: #8c8c8c;
    font-size: 13px;
  }
  .filter-row {
    display: flex;
    gap: 12px;
    margin-bottom: 16px;
    flex-wrap: wrap;
    align-items: center;
  }
  .filter-btn {
    border-radius: 20px;
    &.active {
      background: #00d9a9;
      border-color: #00d9a9;
      color: #fff;
    }
  }
`;

const statusConfig = {
  pending: { color: "gold", text: "Pending" },
  accepted: { color: "green", text: "Accepted" },
  rejected: { color: "red", text: "Rejected" },
};

const AdminInterviewRequests = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchText, setSearchText] = useState("");
  const [actionLoading, setActionLoading] = useState(null);
  const [rejectModalVisible, setRejectModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("authToken");
      const res = await axios.get(API_CONST.GET_INTERVIEW_REQUESTS, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRequests(res.data.requests || []);
    } catch (error) {
      console.error("Error fetching interview requests:", error);
      message.error("Failed to load interview requests");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const handleAccept = (record) => {
    Modal.confirm({
      title: "Accept Interview Request",
      icon: <CheckOutlined style={{ color: "#00d9a9" }} />,
      content: (
        <div>
          <p>Accept interview request for <strong>{record.talent?.name}</strong>?</p>
          <p style={{ color: "#8c8c8c", fontSize: 13 }}>
            This will schedule an interview and notify the partner and customer.
          </p>
        </div>
      ),
      okText: "Accept",
      okButtonProps: { style: { background: "#00d9a9", borderColor: "#00d9a9" } },
      cancelText: "Cancel",
      onOk: async () => {
        setActionLoading(record.id);
        try {
          const token = localStorage.getItem("authToken");
          await axios.put(
            `${API_CONST.ACCEPT_INTERVIEW_REQUEST}/${record.id}/accept`,
            {},
            { headers: { Authorization: `Bearer ${token}` } }
          );
          setRequests((prev) =>
            prev.map((r) => (r.id === record.id ? { ...r, status: "accepted" } : r))
          );
          message.success(`Interview request accepted for ${record.talent?.name}`);
        } catch (error) {
          message.error(error.response?.data?.message || "Failed to accept request");
        } finally {
          setActionLoading(null);
        }
      },
    });
  };

  const handleReject = (record) => {
    setSelectedRequest(record);
    setRejectNotes("");
    setRejectModalVisible(true);
  };

  const handleSubmitReject = async () => {
    setActionLoading(selectedRequest.id);
    try {
      const token = localStorage.getItem("authToken");
      await axios.put(
        `${API_CONST.REJECT_INTERVIEW_REQUEST}/${selectedRequest.id}/reject`,
        { notes: rejectNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRequests((prev) =>
        prev.map((r) =>
          r.id === selectedRequest.id ? { ...r, status: "rejected", notes: rejectNotes } : r
        )
      );
      message.success("Interview request rejected");
      setRejectModalVisible(false);
      setSelectedRequest(null);
    } catch (error) {
      message.error(error.response?.data?.message || "Failed to reject request");
    } finally {
      setActionLoading(null);
    }
  };

  const filteredRequests = requests.filter((r) => {
    const matchesStatus = filterStatus === "all" || r.status === filterStatus;
    const search = searchText.toLowerCase();
    const matchesSearch =
      !search ||
      (r.talent?.name || "").toLowerCase().includes(search) ||
      (r.job?.role || "").toLowerCase().includes(search) ||
      (r.customerOrganization || "").toLowerCase().includes(search) ||
      (r.partnerOrganization || "").toLowerCase().includes(search) ||
      String(r.jobId || "").toLowerCase().includes(search);
    return matchesStatus && matchesSearch;
  });

  const pendingCount = requests.filter((r) => r.status === "pending").length;
  const acceptedCount = requests.filter((r) => r.status === "accepted").length;
  const rejectedCount = requests.filter((r) => r.status === "rejected").length;

  const columns = [
    {
      title: "Request ID",
      dataIndex: "id",
      key: "id",
      width: 160,
      render: (id) => <Tag color="blue">{id}</Tag>,
    },
    {
      title: "Talent",
      key: "talent",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 600 }}>{record.talent?.name || "N/A"}</div>
          <div style={{ fontSize: 12, color: "#8c8c8c" }}>{record.talent?.role || ""}</div>
          {(record.talent?.primarySkills || []).slice(0, 2).map((skill, i) => (
            <Tag key={i} color="blue" style={{ marginTop: 2, fontSize: 11 }}>{skill}</Tag>
          ))}
        </div>
      ),
    },
    {
      title: "Job",
      key: "job",
      width: 200,
      render: (_, record) => (
        <div>
          <div style={{ fontWeight: 500 }}>{record.job?.role || "N/A"}</div>
          <Tag
            color="cyan"
            style={{ fontSize: 11, cursor: "pointer" }}
            onClick={() => navigate(`/admin/job-requirments/job-details/${record.jobId}`)}
          >
            {record.jobId} ↗
          </Tag>
        </div>
      ),
    },
    {
      title: "Customer",
      dataIndex: "customerOrganization",
      key: "customer",
      width: 180,
      render: (org) => org || <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Partner",
      dataIndex: "partnerOrganization",
      key: "partner",
      width: 180,
      render: (org) => org || <span style={{ color: "#bbb" }}>—</span>,
    },
    {
      title: "Experience",
      key: "experience",
      width: 120,
      render: (_, record) => record.talent?.experience || "N/A",
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 130,
      render: (date) =>
        date
          ? new Date(date).toLocaleDateString("en-IN", {
              day: "numeric",
              month: "short",
              year: "numeric",
            })
          : "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      width: 110,
      render: (status) => {
        const cfg = statusConfig[status] || { color: "default", text: status };
        return <Tag color={cfg.color}>{cfg.text}</Tag>;
      },
    },
    {
      title: "Notes",
      dataIndex: "notes",
      key: "notes",
      width: 180,
      render: (notes) =>
        notes ? (
          <span style={{ color: "#595959", fontSize: 12 }}>{notes}</span>
        ) : (
          <span style={{ color: "#bbb" }}>—</span>
        ),
    },
    {
      title: "Action",
      key: "action",
      width: 160,
      render: (_, record) => {
        if (record.status !== "pending") return null;
        return (
          <Space>
            <Button
              type="primary"
              size="small"
              icon={<CheckOutlined />}
              loading={actionLoading === record.id}
              onClick={() => handleAccept(record)}
              style={{ background: "#00d9a9", borderColor: "#00d9a9" }}
            >
              Accept
            </Button>
            <Button
              danger
              size="small"
              icon={<CloseOutlined />}
              loading={actionLoading === record.id}
              onClick={() => handleReject(record)}
            >
              Reject
            </Button>
          </Space>
        );
      },
    },
  ];

  return (
    <Wrapper>
      <div className="page-header">
        <h1>Interview Requests</h1>
        <p>View all interview requests across customers and partners</p>
      </div>

      <Row gutter={16} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <div className="stat-inner">
              <div className="stat-icon" style={{ background: "#fffbe6" }}>
                <span style={{ fontSize: 22, color: "#faad14" }}>⏳</span>
              </div>
              <div className="stat-info">
                <h3>{pendingCount}</h3>
                <p>Pending</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <div className="stat-inner">
              <div className="stat-icon" style={{ background: "#f6ffed" }}>
                <CheckOutlined style={{ fontSize: 22, color: "#52c41a" }} />
              </div>
              <div className="stat-info">
                <h3>{acceptedCount}</h3>
                <p>Accepted</p>
              </div>
            </div>
          </Card>
        </Col>
        <Col xs={24} sm={8}>
          <Card className="stat-card">
            <div className="stat-inner">
              <div className="stat-icon" style={{ background: "#fff1f0" }}>
                <CloseOutlined style={{ fontSize: 22, color: "#ff4d4f" }} />
              </div>
              <div className="stat-info">
                <h3>{rejectedCount}</h3>
                <p>Rejected</p>
              </div>
            </div>
          </Card>
        </Col>
      </Row>

      <div className="filter-row">
        {["all", "pending", "accepted", "rejected"].map((status) => (
          <Button
            key={status}
            className={`filter-btn ${filterStatus === status ? "active" : ""}`}
            onClick={() => setFilterStatus(status)}
          >
            {status.charAt(0).toUpperCase() + status.slice(1)}
            {status === "all"
              ? ` (${requests.length})`
              : ` (${requests.filter((r) => r.status === status).length})`}
          </Button>
        ))}
        <Input
          placeholder="Search talent, job, customer, partner..."
          prefix={<SearchOutlined />}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          allowClear
          style={{ width: 280, marginLeft: "auto" }}
        />
      </div>

      <Card>
        <Spin spinning={loading}>
          <Table
            columns={columns}
            dataSource={filteredRequests}
            rowKey="id"
            pagination={{
              pageSize: 20,
              showSizeChanger: true,
              pageSizeOptions: ["10", "20", "50", "100"],
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} rows`,
            }}
            scroll={{ x: 1500 }}
            locale={{ emptyText: "No interview requests found" }}
          />
        </Spin>
      </Card>

      <Modal
        title="Reject Interview Request"
        open={rejectModalVisible}
        onOk={handleSubmitReject}
        onCancel={() => { setRejectModalVisible(false); setSelectedRequest(null); }}
        okText="Reject"
        okButtonProps={{ danger: true, loading: actionLoading === selectedRequest?.id }}
        cancelText="Cancel"
      >
        {selectedRequest && (
          <div>
            <div style={{ padding: 12, background: "#f8f9fd", borderRadius: 6, marginBottom: 16 }}>
              <p style={{ margin: 0, fontWeight: 500 }}>{selectedRequest.talent?.name}</p>
              <p style={{ margin: "4px 0 0", color: "#8c8c8c", fontSize: 13 }}>
                Job: {selectedRequest.job?.role} ({selectedRequest.jobId})
              </p>
              <p style={{ margin: "4px 0 0", color: "#8c8c8c", fontSize: 13 }}>
                Partner: {selectedRequest.partnerOrganization} · Customer: {selectedRequest.customerOrganization}
              </p>
            </div>
            <label style={{ display: "block", marginBottom: 8, fontWeight: 500, color: "#014c75" }}>
              Reason for rejection (optional)
            </label>
            <Input.TextArea
              rows={3}
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="Enter reason..."
            />
          </div>
        )}
      </Modal>
    </Wrapper>
  );
};

export default AdminInterviewRequests;
